import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as OutlineTrash } from "assets/svg/outline-trash-1.svg";
import ConfirmModal from "components/confirm-modal";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import ErrorMessage from "components/error-message";
import IconButton from "components/icon-button";
import SuccessMessage from "components/success-message";
import ToggleSwitch from "components/toggle-switch";
import React, { useEffect, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import deleteOrchidProReminderById from "services/appointment-reminders/deleteOrchidProReminderById";
import postOrchidProReminder from "services/appointment-reminders/postOrchidProReminder";
import putOrchidProReminderById from "services/appointment-reminders/putOrchidProReminderById";
import queryOrchidProReminders from "services/appointment-reminders/queryOrchidProReminders";
import styles from "./Reminders.module.scss";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const REMINDER_OPTIONS = [
  {
    unit: "minute",
    value: 30,
  },
  {
    unit: "hour",
    value: 1,
  },
  {
    unit: "hour",
    value: 6,
  },
  {
    unit: "hour",
    value: 12,
  },
  {
    unit: "hour",
    value: 24,
  },
];

const CUSTOM_INTERVAL_VALUE_OPTIONS = Array.from(
  { length: 100 },
  (_, i) => i + 1,
);

const CUSTOM_INTERVAL_UNIT_OPTIONS = ["minute", "hour"];

const Reminders = () => {
  const [reminders, setReminders] = useState<
    (AppointmentReminder | AppointmentReminderCreate)[]
  >([]);
  const [changed, setChanged] = useState(false);
  const [success, setSuccess] = useState<string>();
  const [error, setError] = useState<any>();

  const [reminderIdToDelete, setReminderIdToDelete] = useState<number>();
  const [currReminderIndex, setCurrReminderIndex] = useState<number>();

  const { practiceOwnerOrchidUserId } = useTeamContext();

  useEffect(() => {
    queryOrchidProReminders(practiceOwnerOrchidUserId)
      .then((res) => {
        setReminders(
          res
            .filter(
              (reminder) =>
                reminder.marked_deleted_datetime === null &&
                reminder.interval_time_value !== 15,
            )
            .sort((a, b) => a.reminder_id - b.reminder_id),
        );
      })
      .catch(console.error);
  }, [practiceOwnerOrchidUserId]);

  const handleReminderEnableToggle = (index: number) => {
    setChanged(true);

    let nextReminders = reminders;
    nextReminders[index] = {
      ...nextReminders[index],
      enabled: !nextReminders[index].enabled,
    };

    setReminders([...nextReminders]);
  };

  const updateIntervalTime = (
    index: number,
    intervalTimeValue: number,
    intervalTimeUnit: string,
  ) => {
    setChanged(true);

    let nextReminders = reminders;

    nextReminders[index] = {
      ...nextReminders[index],
      interval_time_value: intervalTimeValue,
      interval_time_unit: intervalTimeUnit,
    };

    setReminders([...nextReminders]);
  };

  const updateReminderType = (
    index: number,
    typeId: string,
    value: boolean,
  ) => {
    setChanged(true);

    let nextReminders = reminders;

    nextReminders[index] = {
      ...nextReminders[index],
      [typeId]: value,
    };

    setReminders([...nextReminders]);
  };

  const deleteReminder = async () => {
    if (!reminderIdToDelete) return;

    try {
      await deleteOrchidProReminderById(
        reminderIdToDelete,
        practiceOwnerOrchidUserId,
      );
      setReminders(
        reminders.filter(
          (reminder) =>
            (reminder as AppointmentReminder).reminder_id !==
            reminderIdToDelete,
        ),
      );
      setReminderIdToDelete(undefined);
    } catch (error) {
      setError(error);
    }
  };

  const saveReminderSettings = async () => {
    reminders.forEach(async (reminder) => {
      if ((reminder as AppointmentReminder).reminder_id) {
        try {
          await putOrchidProReminderById(
            (reminder as AppointmentReminder).reminder_id,
            reminder as AppointmentReminder,
            practiceOwnerOrchidUserId,
          );
        } catch (error) {
          setError(error);
          return;
        }
      } else {
        try {
          await postOrchidProReminder(
            reminder as AppointmentReminderCreate,
            practiceOwnerOrchidUserId,
          );
        } catch (error) {
          setError(error);
          return;
        }
      }

      try {
        const response = await queryOrchidProReminders(
          practiceOwnerOrchidUserId,
        );
        setReminders(
          response
            .filter(
              (reminder) =>
                reminder.marked_deleted_datetime === null &&
                reminder.interval_time_value !== 15,
            )
            .sort((a, b) => a.reminder_id - b.reminder_id),
        );
      } catch (error) {
        setError(error);
      } finally {
        setChanged(false);
      }
    });
  };

  return (
    <>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
          className={styles["client-portal-setting__error-message"]}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}

      {currReminderIndex !== undefined && (
        <ConfirmModal
          show={currReminderIndex !== undefined}
          onConfirm={() => setCurrReminderIndex(undefined)}
          onCancel={() => setCurrReminderIndex(undefined)}
          labelTitle={"Custom"}
          labelCancel={"Cancel"}
          labelConfirm={"Select"}
          error={error}
          className={styles["custom-modal"]}
        >
          <DropdownToggle
            className={styles["value-dropdown"]}
            title={reminders[currReminderIndex].interval_time_value || "30"}
            Caret={<OutlineChevronDown />}
          >
            {CUSTOM_INTERVAL_VALUE_OPTIONS.map((val, j) => {
              return (
                <Dropdown.Item
                  key={j}
                  onClick={() => {
                    updateIntervalTime(
                      currReminderIndex,
                      val,
                      reminders[currReminderIndex].interval_time_unit,
                    );
                  }}
                >
                  {val}
                </Dropdown.Item>
              );
            })}
          </DropdownToggle>
          <DropdownToggle
            className={styles["unit-dropdown"]}
            title={
              reminders[currReminderIndex].interval_time_unit + "s" || "minutes"
            }
            Caret={<OutlineChevronDown />}
          >
            {CUSTOM_INTERVAL_UNIT_OPTIONS.map((val, j) => {
              return (
                <Dropdown.Item
                  key={j}
                  onClick={() => {
                    updateIntervalTime(
                      currReminderIndex,
                      reminders[currReminderIndex].interval_time_value,
                      val,
                    );
                  }}
                >
                  {val + "s"}
                </Dropdown.Item>
              );
            })}
          </DropdownToggle>
        </ConfirmModal>
      )}

      <ConfirmModal
        show={reminderIdToDelete !== undefined}
        onConfirm={() => deleteReminder()}
        onCancel={() => setReminderIdToDelete(undefined)}
        labelTitle={"Are you sure?"}
        labelCancel={"Cancel"}
        labelConfirm={"Yes, I confirm"}
        error={error}
      >
        If you delete this setting, it will no longer be available.
      </ConfirmModal>

      <div className={styles["client-portal-setting__body"]}>
        <div className={styles["client-portal-setting__section"]}>
          <div className={styles["client-portal-setting__section-title"]}>
            Session Reminders
          </div>
          <div className={styles["client-portal-setting__content"]}>
            Aside from the custom reminders below, Orchid sends an automatic
            reminder via email 15 minutes before any session by default.
          </div>
          {reminders.map((reminder, i) => {
            return (
              <div key={i} className={styles["reminder-box"]}>
                <div className={styles["reminder-box__header"]}>
                  <div className={styles["reminder-box__toggle"]}>
                    <ToggleSwitch
                      checked={reminder.enabled}
                      onChange={() => {
                        handleReminderEnableToggle(i);
                      }}
                    />
                    Reminder {i + 1}
                  </div>
                  <IconButton
                    onClick={() => {
                      if ((reminder as AppointmentReminder).reminder_id) {
                        setReminderIdToDelete(
                          (reminder as AppointmentReminder).reminder_id,
                        );
                      } else {
                        let nextReminders = reminders;
                        nextReminders.splice(i, 1);
                        setReminders([...nextReminders]);
                      }
                    }}
                  >
                    <OutlineTrash className={"gray01"} />
                  </IconButton>
                </div>
                <hr className={styles["reminder-box__hr"]} />
                <div className={styles["reminder-box__reminder-row"]}>
                  Send a reminder{" "}
                  <DropdownToggle
                    className={styles["reminder-box__dropdown-toggle"]}
                    title={
                      reminder.interval_time_value +
                      " " +
                      reminder.interval_time_unit
                    }
                    Caret={<OutlineChevronDown />}
                  >
                    {REMINDER_OPTIONS.map((option, j) => {
                      return (
                        <Dropdown.Item
                          key={j}
                          onClick={() => {
                            updateIntervalTime(i, option.value, option.unit);
                          }}
                        >
                          {option.value + " " + option.unit + "s"}
                        </Dropdown.Item>
                      );
                    })}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => setCurrReminderIndex(i)}>
                      Custom
                    </Dropdown.Item>
                  </DropdownToggle>
                  before the session.
                </div>

                {[
                  {
                    id: "send_as_email",
                    label: "via Email",
                    value: reminder.send_as_email,
                  },
                  {
                    id: "send_as_sms",
                    label: "via Text Message",
                    value: reminder.send_as_sms,
                  },
                  {
                    id: "send_as_voice_message",
                    label: "via Voice Message",
                    value: reminder.send_as_voice_message,
                  },
                ].map((option, j) => {
                  return (
                    <div
                      key={j}
                      className={styles["reminder-box__type-option"]}
                    >
                      <CustomSelectionControl
                        onChange={(e) => {
                          updateReminderType(i, option.id, e.target.checked);
                        }}
                        type={"checkbox"}
                        checked={option.value}
                      />{" "}
                      {option.label}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <Button
            disabled={reminders.length >= 4}
            className={styles["reminder-box__action"]}
            variant={"link"}
            onClick={() => {
              setChanged(true);
              setReminders([
                ...reminders,
                {
                  enabled: true,
                  interval_time_value: 24,
                  interval_time_unit: "hour",
                  send_as_email: true,
                  send_as_sms: false,
                  send_as_voice_message: false,
                },
              ]);
            }}
          >
            <OutlinePlus16 />
            Add another reminder
          </Button>
        </div>
      </div>
      <div className={styles["client-portal-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          onClick={() => {
            saveReminderSettings();
          }}
          className={styles["client-portal-setting__save-button"]}
          disabled={!changed}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default Reminders;
